/*
  A headline's real line boxes and word gaps, measured so the landing's one
  thread (Thread.tsx) goes round its start and end headlines whatever the
  Mongolian or English line breaks are at any width.
*/

/** One line of a headline, from its real line boxes. */
export interface Line {
  top: number;
  bottom: number;
  left: number;
  right: number;
  /** Centres of the spaces between words on this line. */
  gaps: { x: number; width: number }[];
}

/** The strip's width for a headline whose lines are `lineHeight` tall: a fifth of the type's em, within reason. */
export const stripFor = (lineHeight: number) => Math.max(14, Math.min(38, (lineHeight / 1.2) * 0.21));

/** A headline's lines, top to bottom, in the coordinates of `origin`. */
export function lineBoxes(title: HTMLElement, origin: DOMRect): Line[] {
  const range = document.createRange();
  range.selectNodeContents(title);
  const lines: Line[] = [];
  const lineAt = (top: number, height: number) => lines.find((l) => Math.abs(l.top - top) < height / 2);
  for (const r of Array.from(range.getClientRects())) {
    if (r.width < 2) continue;
    const top = r.top - origin.top;
    const line = lineAt(top, r.height);
    if (line) {
      line.left = Math.min(line.left, r.left - origin.left);
      line.right = Math.max(line.right, r.right - origin.left);
    } else lines.push({ top, bottom: r.bottom - origin.top, left: r.left - origin.left, right: r.right - origin.left, gaps: [] });
  }
  // Word gaps: every space character that sits inside a line rather than at its end.
  const walker = document.createTreeWalker(title, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.textContent ?? "";
    for (let i = 0; i < text.length; i++) {
      if (text[i] !== " ") continue;
      range.setStart(node, i);
      range.setEnd(node, i + 1);
      const r = range.getBoundingClientRect();
      const line = lineAt(r.top - origin.top, r.height || 1);
      const x = r.left - origin.left + r.width / 2;
      if (line && r.width > 0 && x < line.right - r.width) line.gaps.push({ x, width: r.width });
    }
  }
  return lines.sort((a, b) => a.top - b.top);
}
